import { Button, Checkbox, Divider, Flex, Form, Modal, Select } from 'antd'
import { useState } from 'react'
import {
  useArticlesByUserGate,
  useMap,
  useModalDeleteUserStore,
  useUserArticleList,
} from 'src/pages/budget/budgetTeams/hooks'
import {
  closeModalDeleteUser,
  submitDeleteUser,
} from 'src/pages/budget/budgetTeams/model'
import { FieldLabel, UiText } from 'src/pages/budget/components'
import { BUDGET_STEPS, REQUIRED_FIELD } from 'src/pages/budget/const'
import { useUsers } from 'src/pages/budget/hook'
import { filterOption } from 'src/pages/budget/utils'
import type { DeleteUserPayloadModal } from 'src/pages/budget/budgetTeams/types'
import { LoadingOutlined } from '@ant-design/icons'

type Props = {
  data: DeleteUserPayloadModal
}

const DELETE_USER_FORM_ID = 'budget-delete-user-form'

export const ModalDeleteUser = () => {
  const modalDeleteUserStore = useModalDeleteUserStore()
  const { isOpenDeleteModal, data } = modalDeleteUserStore

  return (
    <Modal
      title={<UiText size={20}>Удалить доступ</UiText>}
      centered
      wrapClassName="ant_modal"
      width={600}
      open={isOpenDeleteModal}
      maskClosable={false}
      onCancel={() => closeModalDeleteUser()}
      destroyOnHidden
      footer={
        <Flex gap={8}>
          <Button
            style={{ flex: 1 }}
            size="large"
            onClick={() => closeModalDeleteUser()}
          >
            Отмена
          </Button>
          <Button
            style={{ flex: 1 }}
            danger
            size="large"
            type="primary"
            htmlType="submit"
            form={DELETE_USER_FORM_ID}
          >
            Удалить
          </Button>
        </Flex>
      }
    >
      {data && <ModalDeleteUserContent data={data} />}
    </Modal>
  )
}

const ModalDeleteUserContent = ({ data }: Props) => {
  const [selectedArticles, setSelectedArticles] = useState<number[]>([])
  const { userId, teamId, type } = data

  const { teamMap } = useMap()
  const { allUserBudgetMap } = useUsers()
  useArticlesByUserGate(userId)
  const { userArticleList, loadingUserArticleList } = useUserArticleList()

  const { Item } = Form
  const [form] = Form.useForm<{ replacementUserId: string }>()

  const teamName = teamId && teamMap.get(teamId)?.name
  const userName = allUserBudgetMap.get(userId)

  const options = [...allUserBudgetMap]
    .filter(([id]) => id !== userId)
    .map(([value, label]) => ({ value, label }))

  const toggleArticle = (articleId: number) => {
    setSelectedArticles((prev) =>
      prev.includes(articleId)
        ? prev.filter((id) => id !== articleId)
        : [...prev, articleId],
    )
  }

  const handleOk = (values: { replacementUserId: string }) => {
    submitDeleteUser({
      userId,
      teamId,
      type,
      replacementUserId: values.replacementUserId,
      articleIds: selectedArticles,
    })
    closeModalDeleteUser()
  }

  return (
    <>
      <Divider />

      <Flex vertical>
        <UiText size={18}>
          Вы уверены, что хотите удалить доступ сотрудника{' '}
          <UiText size={18} strong>
            {userName}
          </UiText>{' '}
          к действию «{BUDGET_STEPS[type]}» в команде «{teamName}»?
        </UiText>

        <Form
          id={DELETE_USER_FORM_ID}
          form={form}
          layout="vertical"
          style={{ marginTop: 14 }}
          onFinish={handleOk}
        >
          <Item
            name="replacementUserId"
            style={{ minHeight: 90, marginBottom: 0 }}
            required={false}
            rules={[{ required: true, message: REQUIRED_FIELD }]}
            label={
              <FieldLabel
                hint={
                  'Перед удалением назначьте преемника.\n' +
                  'Ему будут переданы:\n' +
                  '• Черновики заявок на бюджет\n' +
                  '• Незавершённые заявки на бюджет\n' +
                  '• Незавершённые заявки на оплату сотрудника'
                }
              >
                Преемник
              </FieldLabel>
            }
          >
            <Select
              placeholder="Выберите сотрудника"
              style={{ width: '100%' }}
              options={options}
              showSearch
              filterOption={filterOption}
              allowClear
            />
          </Item>
        </Form>

        <Flex vertical gap={12}>
          <FieldLabel hint="Выберите статьи, права доступа к которым будут переданы преемнику.">
            Выберите статьи для передачи прав доступа
          </FieldLabel>

          <Flex vertical gap={10} style={{ maxHeight: 240, overflowY: 'auto' }}>
            {loadingUserArticleList ? (
              <div style={{ height: 25, textAlign: 'center' }}>
                <LoadingOutlined
                  style={{ fontSize: 24, color: 'var(--color-primary)' }}
                />
              </div>
            ) : userArticleList.length === 0 ? (
              <div>Нет доступных статей.</div>
            ) : (
              userArticleList.map(({ id, name }) => (
                <Checkbox
                  key={id}
                  checked={selectedArticles.includes(id)}
                  onChange={() => toggleArticle(id)}
                >
                  <UiText ellipsis size={14}>
                    {name}
                  </UiText>
                </Checkbox>
              ))
            )}
          </Flex>
        </Flex>
      </Flex>

      <Divider />
    </>
  )
}
